import React, { createContext, useCallback, useContext, useState } from 'react'
import { useAgentSchedules } from 'components/Agent/hooks/useAgentSchedules'
import { getTzOffset } from 'components/Agent/model/timezones'

interface IAgentContextValue {
  agentId: string | null
  activeGroupId: string | null
  idToken: string | null
  refreshToken: string | null
  tzName: string | null       // IANA timezone name (e.g. 'Asia/Jerusalem')
  tzDelta: number | null      // DST-aware numeric offset derived from tzName — for display/cron consumers
  whatsappOpen: boolean
  setWhatsappOpen: React.Dispatch<React.SetStateAction<boolean>>
  setAgentId: (id: string | null) => void
  setTokens: (idToken: string, refreshToken: string) => void
  setTzName: (tzName: string | null) => void
  schedules: ReturnType<typeof useAgentSchedules>
}

const AgentContext = createContext<IAgentContextValue>({
  agentId: null,
  activeGroupId: null,
  idToken: null,
  refreshToken: null,
  tzName: null,
  tzDelta: null,
  whatsappOpen: false,
  setWhatsappOpen: () => {},
  setAgentId: () => {},
  setTokens: () => {},
  setTzName: () => {},
  schedules: {
    rows: [],
    loading: false,
    error: '',
    load: async () => {},
    save: async () => undefined,
    remove: async () => false,
  },
})

export const AgentProvider: React.FC<{ children: React.ReactNode; activeGroupId?: string | null }> = ({
  children,
  activeGroupId = null,
}) => {
  const [agentId, setAgentId] = useState<string | null>(null)
  const [idToken, setIdToken] = useState<string | null>(null)
  const [refreshToken, setRefreshToken] = useState<string | null>(null)
  const [tzName, setTzName] = useState<string | null>(null)
  const [whatsappOpen, setWhatsappOpen] = useState(false)

  // Derived: compute current DST-aware offset whenever tzName changes.
  // Numeric offset is still exposed so existing components (SchedulesPanel, AgentSidebar, etc.)
  // keep working without changes.
  const tzDelta = getTzOffset(tzName)

  const schedules = useAgentSchedules(agentId)

  const setTokens = useCallback((id: string, refresh: string) => {
    setIdToken(id)
    setRefreshToken(refresh)
  }, [])

  return (
    <AgentContext.Provider
      value={{
        agentId,
        activeGroupId,
        idToken,
        refreshToken,
        tzName,
        tzDelta,
        whatsappOpen,
        setWhatsappOpen,
        setAgentId,
        setTokens,
        setTzName,
        schedules,
      }}
    >
      {children}
    </AgentContext.Provider>
  )
}

export const useAgentContext = () => useContext(AgentContext)
